import { useMemo } from 'react';
import { useLocation, matchRoutes } from 'react-router-dom';
import { ROUTE_PATTERNS } from './routeManifest';

/**
 * Supplies the `route` prop for <Analytics /> and <SpeedInsights />.
 *
 * Why this exists: outside Next.js, @vercel/analytics and
 * @vercel/speed-insights cannot infer a route PATTERN. Left bare they report
 * the raw URL, so Vercel's Analytics "Routes" tab reads "No route data found"
 * and Speed Insights buckets every sample under a single "Unknown" row.
 * Measured 2026-08-25: 2.4K desktop samples, one row.
 *
 * Reporting the raw pathname instead would be worse than nothing for the five
 * dynamic routes -- /playlists/:id, /create/:slug, /blog/:slug, /invite/:token,
 * /embed/:id -- because every id becomes its own row and the aggregate you
 * actually want ("are playlist detail pages slow?") never forms.
 */

// matchRoutes wants route objects. Built once at module scope: ROUTE_PATTERNS
// is a frozen generated constant, so rebuilding per render would be waste.
const ROUTE_OBJECTS = ROUTE_PATTERNS.map((path) => ({ path }));

/** The pattern react-router reports for the catch-all <Route path="*">. */
export const CATCH_ALL = '*';

/** The bucket every unmatched URL reports as. See resolveUnmatchedRoute. */
export const NOT_FOUND_ROUTE = '/404';

/**
 * Decides what `route` value to report when the visitor is on a URL that no
 * real route matches -- i.e. a 404, where matchRoutes lands on CATCH_ALL.
 *
 * DECIDED 2026-08-27: the fixed bucket. The trade-off as it was originally
 * written, kept because the reasoning is what dates:
 *
 *   - A fixed bucket ('/404'): every bad URL collapses into one row. Clean,
 *     low cardinality, Speed Insights data-point volume stays flat. The stated
 *     cost was losing WHICH urls people hit, so a dead inbound link or a bad
 *     sitemap entry goes invisible.
 *
 *   - `pathname`: you see the exact broken URLs. Cost is unbounded
 *     cardinality -- a crawler probing /wp-admin.php variants mints a row per
 *     request, and Speed Insights Plus bills $6.50 per 100k data points
 *     beyond the allowance.
 *
 *   - `null`: report nothing for 404s at all.
 *
 * WHAT SETTLED IT IS THAT THE BUCKET'S STATED COST IS NOT REAL. <Analytics>
 * receives `route` AND `path` (see App.tsx), and `path` carries the actual
 * pathname. So a fixed bucket keeps cardinality flat in the Routes tab while
 * the raw URL still travels on every beacon. The choice was framed as
 * "clean rows OR broken-link discovery" and it is actually both.
 *
 * `null` is now the ONE option that must not be chosen, and that changed on
 * 2026-08-27 rather than being true when this was written. Because
 * `null !== undefined`, returning null still trips the SDK's
 * disableAutoTrack branch while leaving `route && path` falsy, so 404s report
 * NOTHING rather than falling back to automatic tracking. That is strictly
 * worse than the "Unknown" row the original note assumed it would produce.
 *
 * Speed Insights gains from this too: it drops a sample whose route is null,
 * so 404s were contributing no CWV data at all.
 *
 * @param pathname The unmatched location.pathname, e.g. '/wp-admin.php'
 * @returns The route bucket to report. Never null: see above.
 */
export function resolveUnmatchedRoute(pathname: string): string | null {
  void pathname;
  return NOT_FOUND_ROUTE;
}

/**
 * Pure resolution: pathname -> route pattern. Exported separately from the
 * hook so the drift gate can assert routing behaviour directly instead of
 * going through renderHook and ending up asserting React.
 */
export function resolveRouteFromPathname(pathname: string): string | null {
  const matches = matchRoutes(ROUTE_OBJECTS, pathname);

  // matchRoutes returns null only when the manifest has no catch-all. It does
  // have one, so this branch means the manifest drifted -- treat it the same
  // as a 404 rather than reporting a raw path by accident.
  if (!matches || matches.length === 0) return resolveUnmatchedRoute(pathname);

  // Ranked, so the leaf is the most specific match.
  const matched = matches[matches.length - 1].route.path;
  if (!matched || matched === CATCH_ALL) return resolveUnmatchedRoute(pathname);

  return matched;
}

/**
 * Returns the matched route pattern for the current location, or null when
 * nothing should be reported.
 */
export function useVercelRoute(): string | null {
  const { pathname } = useLocation();
  return useMemo(() => resolveRouteFromPathname(pathname), [pathname]);
}
